import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { proposalsApi, agreementsApi, buyerPortfolioApi, verificationAttentionApi } from '../utils/api';
import Layout from '../components/Layout';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const COLORS = ['#6366f1', '#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe', '#ede9fe'];

const statusColors = {
  draft: 'bg-gray-100 text-gray-700',
  proposed: 'bg-blue-100 text-blue-700',
  negotiating: 'bg-yellow-100 text-yellow-700',
  signed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

// Helper — pick default view when the workspace has activity on both
// sides. Per PRD decision 7: default to whichever side had the most
// recent signing activity in the last 30 days. Phase 1 thin heuristic.
function defaultActiveView({ sellerPortfolio, buyerAgreements }) {
  const buyerCount = buyerAgreements?.length || 0;
  const sellerCount = sellerPortfolio?.summary?.totalAgreements || 0;
  if (sellerCount > 0 && buyerCount === 0) return 'seller';
  if (buyerCount > 0 && sellerCount === 0) return 'buyer';
  const thirty = Date.now() - 30 * 24 * 60 * 60 * 1000;
  const recentBuyer = (buyerAgreements || []).filter((a) => a.signedAt && new Date(a.signedAt).getTime() >= thirty).length;
  const recentSeller = sellerPortfolio?.summary?.agreementsSignedLast30d || 0;
  return recentBuyer > recentSeller ? 'buyer' : 'seller';
}

export default function Dashboard() {
  const { user, workspace } = useAuth();
  const [portfolio, setPortfolio] = useState(null);
  const [buyerAgreements, setBuyerAgreements] = useState([]);
  const [recentProposals, setRecentProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('seller');
  // PRD-030 WU-13 — delivery results awaiting this workspace's confirmation.
  const [attention, setAttention] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Each call is isolated — a single analytics regression must not
        // blank out the whole dashboard. (Past incident: a dropped column in
        // the portfolio query made Recent Proposals render empty.)
        const [proposalsRes, portfolioRes, buyerRes] = await Promise.all([
          proposalsApi.list({ limit: 5 }).catch((e) => {
            console.error('proposals fetch failed:', e);
            return { data: { proposals: [] } };
          }),
          agreementsApi.portfolio().catch((e) => {
            console.error('portfolio fetch failed:', e);
            return { data: {} };
          }),
          buyerPortfolioApi.list().catch(() => ({ data: { agreements: [] } })),
        ]);
        const attentionRes = await verificationAttentionApi.get().catch(() => ({ data: { awaiting_confirmation: [] } }));
        setAttention(attentionRes.data.awaiting_confirmation || []);

        setRecentProposals(proposalsRes.data.proposals || []);
        setPortfolio(portfolioRes.data);
        setBuyerAgreements(buyerRes.data.agreements || []);

        setActiveView(defaultActiveView({
          sellerPortfolio: portfolioRes.data,
          buyerAgreements: buyerRes.data.agreements,
        }));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatFullCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(amount);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  const summary = portfolio?.summary || {};
  const costBreakdown = portfolio?.costBreakdown || [];
  const topByValue = portfolio?.topByValue || [];
  const atRisk = portfolio?.atRisk || [];
  const marginTrend = portfolio?.marginTrend || [];
  const deliverableStats = portfolio?.deliverableStats || {};
  const byOutcomeType = portfolio?.byOutcomeType || [];

  const costPieData = costBreakdown.map(c => ({
    name: c.type_name,
    value: parseFloat(c.total_cost),
  })).filter(c => c.value > 0);

  const trendData = marginTrend.map(m => ({
    month: new Date(m.month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    cost: m.cost,
  }));

  const hasSellerActivity = (summary.totalAgreements || 0) > 0;
  const hasBuyerActivity = buyerAgreements.length > 0;
  const showViewToggle = hasSellerActivity && hasBuyerActivity;

  return (
    <Layout>
      {/* PRD-030 WU-13 — attention: delivery results awaiting confirmation.
          The client already sees them live; confirming finalizes toward
          settlement (F7 rev 2). One line per agreement, one click away. */}
      {attention.length > 0 && (
        <div className="mb-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="attention-confirmations">
          <div className="text-[13.5px] text-emerald-800 font-bold mb-1">
            {attention.length === 1 ? 'A delivery result awaits' : `${attention.length} delivery results await`} your confirmation
          </div>
          {[...new Map(attention.map((a) => [a.agreement_id, a])).values()].map((a) => (
            <div key={a.agreement_id} className="text-[13px] text-emerald-800 flex items-center gap-2">
              <span className="flex-1">{a.agreement_name}</span>
              <Link to={`/agreements/${a.agreement_id}/delivery?tab=progress`} className="font-semibold text-emerald-700 underline">
                Review and confirm
              </Link>
            </div>
          ))}
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="page-header">Welcome back, {user?.name?.split(' ')[0]}</h1>
            <p className="text-gray-600 mt-1">
              {workspace?.name} — {activeView === 'buyer' ? 'Outcomes Across Your Vendors' : 'Portfolio Overview'}
            </p>
          </div>
          {showViewToggle && (
            <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden self-start sm:self-auto">
              <button
                onClick={() => setActiveView('seller')}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeView === 'seller' ? 'bg-indigo-50 text-indigo-700' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Agency view
              </button>
              <button
                onClick={() => setActiveView('buyer')}
                className={`px-4 py-1.5 text-sm font-medium transition-colors ${
                  activeView === 'buyer' ? 'bg-amber-50 text-amber-700' : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Client view
                <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-gray-100 text-gray-600">
                  {buyerAgreements.length}
                </span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Client view — thin cross-vendor wedge per PRD §9 Phase 1. Single
          list, no filters, no heatmap, no trust signals. Phase 3 adds the
          richer dashboard affordances. */}
      {activeView === 'buyer' && (
        <>
          {buyerAgreements.length === 0 ? (
            <div className="card p-12 text-center">
              <p className="text-gray-600">No vendor outcomes yet.</p>
              <p className="text-sm text-gray-600 mt-1">
                Ask an agency to invite you, or visit Connections to start one.
              </p>
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Vendor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Agreement</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Spend to date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Total value</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Next due</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Signed</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {buyerAgreements.map((a) => (
                    <tr key={a.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{a.sellerWorkspace?.name || '—'}</div>
                        {a.sellerWorkspace?.slug && (
                          <div className="text-xs text-gray-600">{a.sellerWorkspace.slug}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Link to={`/agreements/${a.id}`} className="text-primary-600 hover:text-primary-700 font-medium">
                          {a.name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          a.status === 'amended' ? 'bg-gray-100 text-gray-600' : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {a.status === 'amended' ? 'Amended' : 'Active'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatFullCurrency(a.spendToDate)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {formatFullCurrency(a.totalValue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {a.nextDueDate ? new Date(a.nextDueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {a.signedAt ? new Date(a.signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Agency view — existing metrics/charts. */}
      {activeView === 'seller' && (
      <>
      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
        <div className="card p-4">
          <p className="text-xs font-medium text-gray-600">Agreements</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{summary.totalAgreements || 0}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium text-gray-600">Total Revenue</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">
            {formatCurrency(summary.totalRevenue || summary.totalSellRevenue || 0)}
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium text-gray-600">Total Cost</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{formatCurrency(summary.totalCost || 0)}</p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium text-gray-600">Blended Margin</p>
          <p className={`text-2xl font-bold mt-1 ${(summary.marginPercent || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {summary.marginPercent || 0}%
          </p>
        </div>
        <div className="card p-4">
          <p className="text-xs font-medium text-gray-600">Deliverables</p>
          <p className="text-2xl font-bold text-primary-600 mt-1">
            {deliverableStats.completed || 0}/{deliverableStats.total || 0}
          </p>
          <p className="text-xs text-gray-600">{deliverableStats.percent || 0}% complete</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-8">
          {/* Top Agreements by Value */}
          {topByValue.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Top Agreements by Value</h2>
                <Link to="/agreements" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  View all →
                </Link>
              </div>
              <div className="card overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Agreement</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Revenue</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Cost</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {topByValue.slice(0, 5).map((a) => (
                      <tr key={a.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link to={`/agreements/${a.id}`} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                            {a.name}
                          </Link>
                          <p className="text-xs text-gray-600">{a.customer_name}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{formatFullCurrency(a.total_revenue || a.sell_revenue)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatFullCurrency(a.total_cost)}</td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium ${a.marginPercent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {a.marginPercent}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* At Risk */}
          {atRisk.length > 0 && atRisk.some(a => a.marginPercent < 10) && (
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">At Risk (Low Margin)</h2>
              <div className="card overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-600 uppercase">Agreement</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-600 uppercase">Margin</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-600 uppercase">Completion</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {atRisk.filter(a => a.marginPercent < 10).slice(0, 5).map((a) => (
                      <tr key={a.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <Link to={`/agreements/${a.id}`} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                            {a.name}
                          </Link>
                          <p className="text-xs text-gray-600">{a.customer_name}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-medium ${a.marginPercent >= 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {a.marginPercent}%
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-gray-200 rounded-full h-1.5">
                              <div className="bg-primary-600 h-1.5 rounded-full" style={{ width: `${a.completionPercent}%` }}></div>
                            </div>
                            <span className="text-xs text-gray-600">{a.completionPercent}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Recent Proposals */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Recent Proposals</h2>
              <Link to="/proposals" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                View all →
              </Link>
            </div>

            {recentProposals.length > 0 ? (
              <div className="card divide-y divide-gray-100">
                {recentProposals.map((proposal) => (
                  <Link
                    key={proposal.id}
                    to={`/proposals/${proposal.id}`}
                    className="block p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium text-gray-900">{proposal.name}</h3>
                        <p className="text-sm text-gray-600">{proposal.customer_name}</p>
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[proposal.status]}`}>
                        {proposal.status}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="card p-8 text-center">
                <p className="text-gray-600 mb-4">No proposals yet</p>
                <Link to="/proposals/new" className="btn-primary">Create your first proposal</Link>
              </div>
            )}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-8">
          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link to="/proposals/new"
                className="card p-4 flex items-center hover:border-primary-300 transition-colors block">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900">New Proposal</p>
                  <p className="text-sm text-gray-600">Create an outcome-based proposal</p>
                </div>
              </Link>
              <Link to="/cost-input-types"
                className="card p-4 flex items-center hover:border-primary-300 transition-colors block">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-violet-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5M9 11.25v1.5M12 9v3.75m3-6v6" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="font-medium text-gray-900">Cost Input Types</p>
                  <p className="text-sm text-gray-600">Configure your cost structure</p>
                </div>
              </Link>
            </div>
          </div>

          {/* Cost Breakdown Pie Chart */}
          {costPieData.length > 0 && (
            <div className="card p-6">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-4">Cost Breakdown</h3>
              <div className="flex justify-center">
                <PieChart width={200} height={200}>
                  <Pie
                    data={costPieData}
                    cx={100}
                    cy={100}
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {costPieData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatFullCurrency(value)} />
                </PieChart>
              </div>
              <div className="mt-4 space-y-2">
                {costPieData.map((entry, idx) => {
                  const pct = summary.totalCost > 0
                    ? Math.round((entry.value / summary.totalCost) * 100) : 0;
                  return (
                    <div key={idx} className="flex items-center justify-between text-sm">
                      <div className="flex items-center">
                        <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[idx % COLORS.length] }}></div>
                        <span className="text-gray-600">{entry.name}</span>
                      </div>
                      <span className="text-gray-900 font-medium">{pct}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Outcome Type Performance */}
          {byOutcomeType.length > 0 && (
            <div className="card p-6">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-4">By Outcome Type</h3>
              <div className="space-y-3">
                {byOutcomeType.slice(0, 5).map((ot, idx) => (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-700 font-medium">{ot.outcome_type_name}</span>
                      <span className={`font-medium ${ot.margin_percent >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {ot.margin_percent}%
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-gray-600">
                      <span>{formatFullCurrency(ot.total_revenue)} revenue</span>
                      <span>{ot.agreement_count} deal{ot.agreement_count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cost Trend */}
          {trendData.length > 1 && (
            <div className="card p-6">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-4">Cost Trend</h3>
              <ResponsiveContainer width="100%" height={160}>
                <LineChart data={trendData}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v >= 1000 ? `${(v/1000).toFixed(0)}K` : v}`} />
                  <Tooltip formatter={(value) => formatFullCurrency(value)} />
                  <Line type="monotone" dataKey="cost" stroke="#6366f1" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Deliverable Progress */}
          {deliverableStats.total > 0 && (
            <div className="card p-6">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-4">Deliverable Completion</h3>
              <div className="flex items-center gap-4">
                <div className="relative w-20 h-20">
                  <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#6366f1"
                      strokeWidth="3"
                      strokeDasharray={`${deliverableStats.percent}, 100`}
                    />
                  </svg>
                  <span className="absolute inset-0 flex items-center justify-center text-lg font-bold text-gray-900">
                    {deliverableStats.percent}%
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  <p><span className="font-medium text-gray-900">{deliverableStats.completed}</span> completed</p>
                  <p><span className="font-medium text-gray-900">{deliverableStats.total - deliverableStats.completed}</span> remaining</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </Layout>
  );
}
